import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { HasAccess } from "@/components/common/has-access";
import { toast } from "@/hooks/use-toast";
import { paymentPageSchema } from "@/features/payment-pages/ui/payment-page-form";
import { useCreatePaymentPage } from "@/features/payment-pages/hooks/use-create-payment-page";
import { checkSlugAvailability } from "@/features/payment-pages/services/check-slug-availability";
import useBundlesGrouped from "@/features/bundles/hooks/use-bundles-grouped";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import { useRegionFilter } from "@/features/regions/hooks/use-region-filter";
import { ALL_REGIONS_CODE, getStoredRegion } from "@/features/regions/constants";
import BundleWizardStepper, {
  WizardStep,
} from "@/features/bundles/ui/wizard/bundle-wizard-stepper";
import StepOffering, {
  OfferingMode,
} from "@/features/payment-pages/ui/wizard/step-offering";
import StepPageDetails from "@/features/payment-pages/ui/wizard/step-page-details";
import StepPricing from "@/features/payment-pages/ui/wizard/step-pricing";
import StepReview from "@/features/payment-pages/ui/wizard/step-review";
import { PaymentPageWizardData } from "@/features/payment-pages/ui/wizard/wizard-form";
import { PaymentPageFormData } from "@/pages/payment-pages/types/payment-page";
import { GroupedBundleLeaf } from "@/pages/bundles/types/grouped-bundle";

const STEPS: WizardStep[] = [
  { key: "offering", label: "Offering" },
  { key: "details", label: "Page details" },
  { key: "pricing", label: "Pricing" },
  { key: "review", label: "Review" },
];

const STEP_FIELDS: Record<number, (keyof PaymentPageWizardData)[]> = {
  1: ["slug", "page_id", "page_title", "status"],
  2: ["currency"],
};

export default function PaymentPageCreate() {
  const { region: regionSegment } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const prefillRegionCode = useRegionFilter();
  const { data: regions } = useActiveRegions();
  const { data: grouped } = useBundlesGrouped({});

  const createPaymentPage = useCreatePaymentPage();

  const [current, setCurrent] = useState(0);
  const [mode, setMode] = useState<OfferingMode>("link");
  const [checkingSlug, setCheckingSlug] = useState(false);

  const form = useForm<PaymentPageWizardData>({
    resolver: zodResolver(paymentPageSchema),
    defaultValues: {
      // Seeded from the bundle wizard chain (?slug=) so the page reuses the
      // slug the bundle's payment_page_link was built from.
      slug: searchParams.get("slug") ?? "",
      region: "",
      region_id: null,
      page_id: "",
      page_title: "",
      fallback_amount: null,
      currency: "INR",
      bundle_standard_id: null,
      redirect_link: null,
      login_redirect_url: null,
      is_auth_enabled: false,
      description: "",
      plan_details: "",
      tnc: "",
      how_it_works: "",
      status: "ACTIVE",
    },
  });

  // All active offerings (one current bundle per standard_id), for restoring
  // a selection from the query param after a reload.
  const activeBundles = useMemo(
    () =>
      (grouped?.groups ?? [])
        .flatMap((g) => g.offerings)
        .map((o) => o.current)
        .filter((b): b is GroupedBundleLeaf => Boolean(b)),
    [grouped],
  );

  const applyOffering = (b: GroupedBundleLeaf) => {
    form.setValue("bundle_standard_id", b.standard_id);
    form.setValue("region", b.region_rel?.code ?? b.region ?? "");
    form.setValue("region_id", b.region_rel?.id ?? b.region_id ?? null);
    form.setValue("currency", b.currency);
    form.setValue("fallback_amount", b.amount);
    if (!form.getValues("page_title")) form.setValue("page_title", b.name);

    const next = new URLSearchParams(searchParams);
    next.set("offering", b.standard_id);
    setSearchParams(next, { replace: true });
  };

  // Page-owned mode switch: clear the offering + query param on manual.
  const handleSetMode = (m: OfferingMode) => {
    setMode(m);
    if (m === "manual") {
      form.setValue("bundle_standard_id", null);
      const next = new URLSearchParams(searchParams);
      next.delete("offering");
      setSearchParams(next, { replace: true });
    }
  };

  // Restore an offering selection from ?offering= on reload, jumping to step 2.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    const std = searchParams.get("offering");
    if (!std || activeBundles.length === 0) return;
    const b = activeBundles.find((x) => x.standard_id === std);
    // Not in the fetched set yet (e.g. a just-created bundle whose grouped query
    // is still refetching) — don't mark restored; retry when the data updates.
    if (!b) return;
    applyOffering(b);
    setMode("link");
    setCurrent(1);
    restoredRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBundles]);

  // Prefill region (+ currency) for manual mode; a picked offering overrides it.
  // When the global toggle is "All", fall back to the last-used region instead
  // of leaving it blank (mirrors the bundle wizard).
  useEffect(() => {
    if (!regions) return;
    if (form.getValues("region_id")) return;
    const stored = getStoredRegion();
    const code =
      prefillRegionCode ??
      (stored && stored !== ALL_REGIONS_CODE ? stored : undefined);
    if (!code) return;
    const r = regions.find((x) => x.code === code);
    if (r) {
      form.setValue("region_id", r.id);
      form.setValue("region", r.code);
      form.setValue("currency", r.currency);
    }
  }, [regions, prefillRegionCode, form]);

  const goBackToList = () =>
    navigate(`/${regionSegment ?? "all"}/payment-pages`);

  const handleNext = async () => {
    if (current === 0) {
      if (mode === "link" && !form.getValues("bundle_standard_id")) {
        toast({
          title: "Offering required",
          description: "Select an offering to continue.",
          variant: "destructive",
        });
        return;
      }
      if (mode === "manual" && !form.getValues("region_id")) {
        toast({
          title: "Region required",
          description: "Pick a region to continue.",
          variant: "destructive",
        });
        return;
      }
    }

    const fields = STEP_FIELDS[current];
    const ok = fields ? await form.trigger(fields) : true;
    if (!ok) return;

    // Page details → verify the slug is unique before advancing.
    if (current === 1) {
      const slug = (form.getValues("slug") || "").trim();
      setCheckingSlug(true);
      try {
        const available = await checkSlugAvailability(slug);
        if (!available) {
          form.setError("slug", {
            type: "manual",
            message: "Slug already exists",
          });
          return;
        }
      } catch {
        // On a network error don't hard-block; create-time uniqueness still guards.
      } finally {
        setCheckingSlug(false);
      }
    }

    setCurrent((c) => Math.min(c + 1, STEPS.length - 1));
  };

  const onSubmit = async (data: PaymentPageWizardData) => {
    if (current !== STEPS.length - 1) return;
    try {
      await createPaymentPage.mutateAsync(data as PaymentPageFormData);
      toast({ title: "Success", description: "Payment page created" });
      goBackToList();
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to create payment page",
        variant: "destructive",
      });
    }
  };

  const isLastStep = current === STEPS.length - 1;

  return (
    <HasAccess permission="write">
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <button
          type="button"
          onClick={goBackToList}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" /> Payment Pages / New Page
        </button>

        <BundleWizardStepper steps={STEPS} current={current} />

        <Form {...form}>
          {/* Swallow implicit submits; creation only via the explicit button. */}
          <form
            onSubmit={(e) => e.preventDefault()}
            className="space-y-6 rounded-lg border border-gray-200 p-6"
          >
            {current === 0 && (
              <StepOffering
                form={form}
                mode={mode}
                setMode={handleSetMode}
                onPickBundle={applyOffering}
              />
            )}
            {current === 1 && <StepPageDetails form={form} />}
            {current === 2 && <StepPricing form={form} />}
            {current === 3 && <StepReview form={form} />}

            <div className="flex justify-between pt-4 border-t border-gray-100">
              <Button
                type="button"
                variant="outline"
                onClick={
                  current === 0 ? goBackToList : () => setCurrent((c) => c - 1)
                }
              >
                {current === 0 ? "Cancel" : "← Back"}
              </Button>

              {isLastStep ? (
                <Button
                  type="button"
                  disabled={createPaymentPage.isPending}
                  onClick={form.handleSubmit(onSubmit)}
                >
                  {createPaymentPage.isPending
                    ? "Creating…"
                    : "✓ Create Payment Page"}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleNext}
                  disabled={checkingSlug}
                >
                  {checkingSlug
                    ? "Checking…"
                    : `Next: ${STEPS[current + 1].label} →`}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </div>
    </HasAccess>
  );
}
